import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import { acceptInviteAPI, verifyTokenApi } from "../_request";
import { useAuth } from "context";
import CircularProgress from "@mui/material/CircularProgress";
import { useAnalyticsEvent } from "hooks/useAnalyticsEvent";
import { AnalyticsEvents } from "core/constant";
import "../../style.css";

function AcceptInvite() {
  const tokenCode = new URL(window.location.href).searchParams.get("token") || null;
  const { saveAuthData, showSnackbarWithMessage } = useAuth();
  const [loader, setLoader] = useState(true);
  const [error, setError] = useState(undefined);
  const [storeStatus, setstoreStatus] = useState();
  const [apiResponseData, setApiResponseData] = useState(null);
  const navigate = useNavigate();
  const [count, setCount] = useState(10);
  const [countVisible, setCountVisible] = useState(true);
  const [isApiSuccess, setIsApiSuccess] = useState(false);

  const { trackCustomEvent } = useAnalyticsEvent();

  const handleAcceptInvite = async () => {
    const {
      data: { success, data, message },
    } = await acceptInviteAPI({ token: tokenCode });

    // Store API response data for later use
    setApiResponseData(data);

    if (success) {
      const authData = data.tokens;
      const verifiedCustomer = await saveAuthData(authData);
      if (!!verifiedCustomer && verifiedCustomer.verifiedAt) {
        setLoader(false);
        setIsApiSuccess(true);
        trackCustomEvent(
          AnalyticsEvents.CUSTOMER_INVITE_ACCEPTED,
          {
            invite_id: data.invite_id,
            ...(data.campaign_code && { campaign_code: data.campaign_code }),
            user_id: verifiedCustomer.id,
            user_role: verifiedCustomer.role,
            user_type: verifiedCustomer.userType,
          },
          false
        );
        return;
      } else if (!!verifiedCustomer) {
        setLoader(false);
        navigate("/authentication/verify-customer");
        showSnackbarWithMessage({
          type: "success",
          message: "You're in! To complete the process, please verify your email.",
        });
        return;
      } else {
        setLoader(false);
        setError(message);
        showSnackbarWithMessage({
          type: "error",
          message: "something went wrong",
        });
      }
    } else {
      if (data && data?.tokens) {
        const authData = data.tokens;
        await saveAuthData(authData);
      }
      setstoreStatus(message);
      showSnackbarWithMessage({
        type: "error",
        message,
      });
      setError(message);
      setLoader(false);
    }
  };

  const handleSignUpRedirect = () => {
    if (apiResponseData?.restaurantOnboardingLink) {
      window.location.href = apiResponseData.restaurantOnboardingLink;
    }
  };

  useEffect(() => {
    handleAcceptInvite();
  }, []);

  useEffect(() => {
    if (isApiSuccess) {
      const intervalId = setInterval(() => {
        setCountVisible(false);
        setTimeout(() => {
          setCount((prevCount) => prevCount - 1);
          setCountVisible(true);
        }, 500);
      }, 1000);

      if (count === 0) {
        clearInterval(intervalId);
        navigate("/dashboard");
        showSnackbarWithMessage({
          type: "success",
          message: "Login successful. Welcome to the Catering Rewards!",
        });
      }

      return () => clearInterval(intervalId);
    }
  }, [isApiSuccess, count, navigate, showSnackbarWithMessage]);

  const renderExpiredInviteMessage = () => (
    <MDBox sx={{ textAlign: "center", padding: "40px 20px", maxWidth: "620px" }}>
      <MDTypography variant="h4" fontWeight="bold" color="text" mb={2}>
        Whoops! This invite has expired, but you&lsquo;re not out of luck.
      </MDTypography>
      <MDTypography variant="body1" color="text" mb={1}>
        You can still earn up to{" "}
        <MDTypography component="span" variant="body1" fontWeight="bold" color="success">
          {apiResponseData?.highestCashbackReward || "XX"}
        </MDTypography>{" "}
        rewards on every order!
      </MDTypography>
      <MDTypography variant="body1" color="text" mb={3}>
        Just sign up, place your first order, and start earning.
      </MDTypography>
      <MDButton
        variant="gradient"
        color="success"
        size="large"
        onClick={handleSignUpRedirect}
        sx={{
          backgroundColor: "#238856",
          "&:hover": {
            backgroundColor: "#1e7049",
          },
          borderRadius: "8px",
          padding: "12px 32px",
          fontSize: "16px",
          fontWeight: "bold",
        }}
      >
        👉 Sign Up Now
      </MDButton>
    </MDBox>
  );

  const renderErrorMessage = () => (
    <MDBox sx={{ textAlign: "center", padding: "40px 20px", maxWidth: "620px" }}>
      <MDTypography variant="h4" fontWeight="bold" color="text" mb={2}>
        Whoops! This invite has expired, but you&lsquo;re not out of luck.
      </MDTypography>
      <MDTypography variant="body1" color="text" mb={1}>
        You can still earn up to{" "}
        <MDTypography component="span" variant="body1" fontWeight="bold" color="success">
          {apiResponseData?.highestCashbackReward + "X" || "20X"}
        </MDTypography>{" "}
        rewards on every order!
      </MDTypography>
      <MDTypography variant="body1" color="text" mb={3}>
        Just sign up, place your first order, and start earning.
      </MDTypography>
      <MDButton
        variant="gradient"
        color="success"
        size="large"
        onClick={handleSignUpRedirect}
        sx={{
          backgroundColor: "#238856",
          "&:hover": {
            backgroundColor: "#1e7049",
          },
          borderRadius: "8px",
          padding: "12px 32px",
          fontSize: "16px",
          fontWeight: "bold",
        }}
      >
        👉 Sign Up Now
      </MDButton>
    </MDBox>
  );

  return (
    <MDBox
      width={"100%"}
      height="100%"
      minHeight="100vh"
      sx={{ overflowX: "hidden", display: "flex", justifyContent: "center", alignItems: "center" }}
    >
      {loader && <CircularProgress color="success" />}
      {storeStatus === "Invite Accepted" && !loader ? (
        <MDBox sx={{ textAlign: "center", padding: "20px" }}>
          <h2>Invite has already been accepted</h2>
          <p>
            You can reset your password for security using the temporary password sent to your
            email.
          </p>
          <p>Please check your spam folder if you don&apos;t see our email.</p>
          <p>
            <MDTypography
              component={Link}
              to="/authentication/sign-in"
              variant="button"
              color="info"
              fontWeight="medium"
              textGradient
            >
              Click here
            </MDTypography>{" "}
            to proceed.
          </p>
        </MDBox>
      ) : storeStatus === "Invite Expired" && !loader ? (
        renderExpiredInviteMessage()
      ) : storeStatus === "Thanks! You've already joined using this invite" && !loader ? (
        <MDBox sx={{ textAlign: "center", padding: "20px" }}>
          <h2>Thanks! You&apos;ve already joined using this invite.</h2>
          <p>
            <MDTypography
              component={Link}
              to="/authentication/sign-in"
              variant="button"
              color="info"
              fontWeight="medium"
              textGradient
              sx={{ fontSize: "20px" }}
            >
              Click here
            </MDTypography>{" "}
            to login in portal
          </p>
        </MDBox>
      ) : error && !loader ? (
        renderErrorMessage()
      ) : (
        ""
      )}
      {!error && !loader && (
        <MDBox sx={{ textAlign: "center", padding: "20px" }}>
          <h2>Welcome to Our Catering Rewards Program!</h2>
          <p>
            Your account setup is underway. You&apos;ll be redirected to your dashboard shortly.
            <br></br>
          </p>
          <br></br>
          <h4>Enjoy your rewards journey!</h4>
          <p>
            The page will redirect in{" "}
            <span className={`countdown ${countVisible ? "visible" : ""}`}>{count}</span> seconds.
            If not,{" "}
            <MDTypography
              component={Link}
              to="/dashboard"
              variant="button"
              color="info"
              fontWeight="medium"
              textGradient
            >
              Click here
            </MDTypography>{" "}
            to reset your password.
          </p>
        </MDBox>
      )}
    </MDBox>
  );
}
export default AcceptInvite;
